import { defineStore } from 'pinia';
import { ref } from 'vue'
import { searchApi } from '@/shared/api/search'

export interface Result {
  type: 'merch' | 'preorder' | 'fandom';
  entity_id: number;
  title: string;
  image_url: string;
  relevance: number;
  priority: number;
}

export const FANDOM_MIN_PREORDERS = 3;

export function collapseFandoms(results: Result[]): Result[] {
  const titleCounts = new Map<string, number>();
  results.forEach((result) => {
    if (result.type !== 'preorder') return;
    const title = result.title.trim().toLowerCase();
    titleCounts.set(title, (titleCounts.get(title) ?? 0) + 1);
  });

  const alreadyCollapsed = new Set<string>();

  return results.reduce<Result[]>((collapsed, result) => {
    const title = result.title.trim().toLowerCase();

    if (result.type !== 'preorder' || (titleCounts.get(title) ?? 0) < FANDOM_MIN_PREORDERS) {
      collapsed.push(result);
      return collapsed;
    }

    if (!alreadyCollapsed.has(title)) {
      alreadyCollapsed.add(title);
      collapsed.push({ ...result, type: 'fandom', entity_id: 0, image_url: '' });
    }

    return collapsed;
  }, []);
}

export const useSearch = defineStore('search', () => {
  const results = ref<Result[]>([]);
  const loading = ref(false);
  const error = ref<Error | null>(null);
  let abortController: AbortController | null = null;

  const fetchResult = async (query: string): Promise<void> => {
    // Отменяем предыдущий запрос, если он есть
    abortController?.abort();
    abortController = new AbortController();

    loading.value = true;
    error.value = null;

    try {
      results.value = collapseFandoms(
        (await searchApi.search(query, abortController.signal))
          .sort((a, b) => a.relevance - b.relevance)
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      error.value = err as Error;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    results,
    loading,
    error,
    fetchResult,
  };
});
